"use client";

import { Send } from "lucide-react";
import { useState } from "react";
import type { ShareEntityType } from "@/hooks/use-chat";
import { trackEntityShare } from "@/lib/analytics/share";
import { cn } from "@/lib/utils";
import {
  type NativeSharePayload,
  SendToChatDrawer,
} from "./send-to-chat-drawer";

interface SendInChatButtonProps {
  entityType: ShareEntityType;
  entityId: string;
  className?: string;
  children?: React.ReactNode;
  nativeShare?: NativeSharePayload | null;
  "aria-label"?: string;
}

/**
 * Drop-in "Send in chat" trigger for entity pages — renders its own
 * SendToChatDrawer. Pass children to fully control the button contents.
 */
export function SendInChatButton({
  entityType,
  entityId,
  className,
  children,
  nativeShare,
  "aria-label": ariaLabel = "Send in chat",
}: SendInChatButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          // Every entity share funnels through this trigger, so intent is
          // captured once here rather than in each detail page.
          trackEntityShare(entityType, entityId, { surface: "entity_page" });
          setOpen(true);
        }}
        className={cn(className)}
        aria-label={ariaLabel}
      >
        {children ?? <Send className="h-4.5 w-4.5" />}
      </button>

      <SendToChatDrawer
        open={open}
        onOpenChange={setOpen}
        payload={{ type: "entity", entityType, entityId }}
        nativeShare={nativeShare}
      />
    </>
  );
}
